import React from 'react';
import { Button, Segmented, Space, Tooltip } from 'antd';
import { FolderOpenOutlined, UploadOutlined } from '@ant-design/icons';
import type { CompositionEditorFilter } from '../../lib/compositionEditorAdapter';

export type CompositionToolbarDocumentActions = {
  /** Show action buttons (requires edit gate open). */
  visible: boolean;
  onAddDocument: () => void;
  onUploadDocument: () => void;
  addDocumentLoading?: boolean;
  uploadDocumentLoading?: boolean;
  /** When false, upload button stays visible but disabled. */
  canUploadDocument: boolean;
  uploadDisabledReason?: string;
};

export type CompositionToolbarProps = {
  filter: CompositionEditorFilter;
  onFilterChange: (filter: CompositionEditorFilter) => void;
  documentActions?: CompositionToolbarDocumentActions;
};

const UPLOAD_PERMISSION_TOOLTIP = 'Нет права загрузки документов';

/**
 * Filter surface + document command buttons (CP-FIN-002-1 / CP-022).
 * Upload / picker orchestration lives in parent hooks — toolbar renders actions only.
 */
const CompositionToolbar: React.FC<CompositionToolbarProps> = ({
  filter,
  onFilterChange,
  documentActions,
}) => {
  const showDocumentActions = documentActions?.visible === true;
  const documentActionLoading =
    documentActions?.addDocumentLoading === true ||
    documentActions?.uploadDocumentLoading === true;
  const uploadDisabled =
    !documentActions?.canUploadDocument || documentActionLoading;
  const addDocumentTooltip = documentActions?.addDocumentLoading
    ? 'Открываем реестр документов'
    : 'Выбрать существующий документ из реестра';
  const uploadTooltip =
    documentActions?.canUploadDocument === false
      ? (documentActions.uploadDisabledReason ?? UPLOAD_PERMISSION_TOOLTIP)
      : documentActions?.uploadDocumentLoading
        ? 'Действие выполняется'
        : 'Загрузить новый документ в реестр';

  return (
    <div className="cp-composition__toolbar" data-testid="composition-toolbar">
      <div className="cp-composition__toolbar-filters">
        <Segmented
          size="small"
          value={filter}
          onChange={(value) => onFilterChange(value as CompositionEditorFilter)}
          options={[
            { label: 'Все', value: 'all' },
            { label: 'Обязательные', value: 'required' },
            { label: 'Проблемные', value: 'problems' },
            { label: 'Исключенные', value: 'excluded' },
          ]}
        />
      </div>

      {showDocumentActions ? (
        <div className="cp-composition__toolbar-actions">
          <Space size={8} wrap className="cp-composition__toolbar-command-group">
            <Tooltip title={addDocumentTooltip}>
              <Button
                size="small"
                icon={<FolderOpenOutlined />}
                onClick={documentActions.onAddDocument}
                loading={documentActions.addDocumentLoading}
                disabled={documentActionLoading}
                aria-label="Добавить существующий документ"
                data-testid="composition-toolbar-add-document"
              >
                Добавить существующий документ
              </Button>
            </Tooltip>
            <Tooltip title={uploadTooltip}>
              <Button
                type="primary"
                size="small"
                icon={<UploadOutlined />}
                onClick={documentActions.onUploadDocument}
                loading={documentActions.uploadDocumentLoading}
                disabled={uploadDisabled}
                aria-label="Загрузить документ"
                data-testid="composition-toolbar-upload-document"
              >
                Загрузить документ
              </Button>
            </Tooltip>
          </Space>
        </div>
      ) : null}
    </div>
  );
};

export default CompositionToolbar;
